import { useState } from 'react';
import { api } from '../api/client';

export interface CheckedSource {
  checkStatus?: string | null;
  checkMessage?: string | null;
  checkedAt?: string | null;
}

/** One verdict for a channel: OK when any source plays (the TV falls back between sources). */
export function streamVerdict(sources: CheckedSource[]): { status: 'OK' | 'FAIL' | 'UNCHECKED' | null; message: string; at: string | null } {
  const checked = sources.filter((s) => s.checkStatus);
  const pick = checked.find((s) => s.checkStatus === 'OK') ?? checked.find((s) => s.checkStatus === 'FAIL') ?? checked[0];
  if (!pick) return { status: null, message: 'Not checked yet', at: null };
  const at = checked.map((s) => s.checkedAt ?? '').sort().pop() || null;
  return { status: pick.checkStatus as 'OK' | 'FAIL' | 'UNCHECKED', message: pick.checkMessage ?? '', at };
}

const LABEL = { OK: 'Streaming', FAIL: 'Not streaming', UNCHECKED: 'Not testable' } as const;
const CLASS = { OK: 'ok', FAIL: 'danger', UNCHECKED: 'warn' } as const;

export function StreamBadge({ sources, checking }: { sources: CheckedSource[]; checking?: boolean }) {
  if (checking) return <span className="badge">Checking…</span>;
  const v = streamVerdict(sources);
  if (!v.status) return <span className="badge" title={v.message}>Not checked</span>;
  const when = v.at ? new Date(v.at).toLocaleString() : '';
  return (
    <span className={`badge ${CLASS[v.status]}`} title={`${v.message}${when ? ` · checked ${when}` : ''}`}>
      {LABEL[v.status]}
    </span>
  );
}

/**
 * Runs the server's stream check in small batches (each call stays short) and reports progress.
 * kind = which ids these are: lineup entries or channels.
 */
export function useStreamCheck(kind: 'siteChannelIds' | 'channelIds', onBatchDone: () => void) {
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const run = async (ids: string[]) => {
    if (!ids.length) return;
    setError(null);
    setPending(new Set(ids));
    setProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i += 20) {
        const batch = ids.slice(i, i + 20);
        await api.post('/streams/check', { [kind]: batch });
        setPending((cur) => {
          const n = new Set(cur);
          batch.forEach((id) => n.delete(id));
          return n;
        });
        setProgress({ done: Math.min(i + 20, ids.length), total: ids.length });
        onBatchDone();
      }
    } catch (e) {
      setError(e);
    } finally {
      setPending(new Set());
      setProgress(null);
    }
  };
  return { run, pending, progress, error, busy: progress !== null };
}

/** "12 streaming · 3 not streaming · 4 not testable · 5 not checked" for a list of channels. */
export function StreamSummary({ verdicts }: { verdicts: ReturnType<typeof streamVerdict>[] }) {
  const n = (s: string | null) => verdicts.filter((v) => v.status === s).length;
  const parts = [
    n('OK') && <span key="ok" className="badge ok">{n('OK')} streaming</span>,
    n('FAIL') && <span key="fail" className="badge danger">{n('FAIL')} not streaming</span>,
    n('UNCHECKED') && <span key="un" className="badge warn">{n('UNCHECKED')} not testable</span>,
    n(null) && <span key="nc" className="badge">{n(null)} not checked</span>,
  ].filter(Boolean);
  return <span className="row" style={{ gap: 6 }}>{parts}</span>;
}
